import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RevisionModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [feedback, setFeedback] = useState('');
  const [revisionType, setRevisionType] = useState('content');

  const revisionTypes = [
    {
      value: 'content',
      label: 'تغییر محتوا',
      description: 'اصلاح متن و محتوای سند',
    },
    {
      value: 'format',
      label: 'تغییر قالب',
      description: 'تغییر ساختار و قالب‌بندی',
    },
    {
      value: 'legal',
      label: 'بررسی حقوقی',
      description: 'اصلاح نکات حقوقی و قانونی',
    },
    {
      value: 'language',
      label: 'ویرایش زبان',
      description: 'بهبود نگارش و املا',
    },
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (feedback?.trim()) {
      onSubmit({
        type: revisionType,
        feedback: feedback?.trim(),
        timestamp: new Date()?.toISOString(),
      });
      setFeedback('');
      setRevisionType('content');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-shadow">
        {/* Header */}
        <div className="border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="MessageSquare" size={20} className="text-warning" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">
                  درخواست بازنگری
                </h2>
                <p className="text-sm text-muted-foreground">
                  نظرات و پیشنهادات خود را برای بهبود سند ارسال کنید
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground">
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Revision Type Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                نوع بازنگری مورد نظر
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {revisionTypes?.map((type) => (
                  <label
                    key={type?.value}
                    className={`
                      relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-smooth
                      ${
                        revisionType === type?.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }
                    `}>
                    <input
                      type="radio"
                      name="revisionType"
                      value={type?.value}
                      checked={revisionType === type?.value}
                      onChange={(e) => setRevisionType(e?.target?.value)}
                      className="sr-only"
                    />
                    <div
                      className={`
                      w-4 h-4 rounded-full border-2 mt-0.5 transition-smooth
                      ${
                        revisionType === type?.value
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground'
                      }
                    `}>
                      {revisionType === type?.value && (
                        <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-foreground">
                        {type?.label}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {type?.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Feedback Input */}
            <div>
              <Input
                label="توضیحات و پیشنهادات"
                type="textarea"
                placeholder="لطفاً توضیح دهید که چه تغییراتی در سند مورد نظر شماست..."
                value={feedback}
                onChange={(e) => setFeedback(e?.target?.value)}
                required
                className="min-h-32"
                description="حداقل ۲۰ کاراکتر وارد کنید تا بتوانیم بهترین پاسخ را ارائه دهیم"
              />
            </div>

            {/* Info Box */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-foreground mb-1">نکات مهم:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• درخواست بازنگری معمولاً ۲۴ تا ۴۸ ساعت زمان می‌برد</li>
                    <li>• هر درخواست حداکثر ۳ بار قابل بازنگری است</li>
                    <li>• توضیحات دقیق‌تر منجر به نتیجه بهتری می‌شود</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              disabled={!feedback?.trim() || feedback?.length < 20}
              iconName="Send"
              iconPosition="right"
              className="sm:flex-1">
              ارسال درخواست بازنگری
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="sm:w-auto">
              انصراف
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RevisionModal;
